import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Search, Star, ShoppingCart, Filter, Heart } from "lucide-react";
import { toast } from "sonner";

const CATS = [
    { id: "", label: "All" },
    { id: "detergents", label: "Detergents" },
    { id: "ict", label: "ICT Services" },
    { id: "software", label: "Software" },
];

const SORTS = [
    { id: "newest", label: "Newest" },
    { id: "price-asc", label: "Price: Low → High" },
    { id: "price-desc", label: "Price: High → Low" },
    { id: "rating", label: "Top rated" },
];

export default function Products() {
    const [params, setParams] = useSearchParams();
    const category = params.get("category") || "";
    const search = params.get("search") || "";
    const sort = params.get("sort") || "newest";
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { add } = useCart();
    const { has, toggle } = useWishlist();
    const { format } = useCurrency();
    const [q, setQ] = useState(search);

    useEffect(() => {
        setLoading(true);
        const qs = new URLSearchParams();
        if (category) qs.set("category", category);
        if (search) qs.set("search", search);
        api.get(`/products?${qs.toString()}`)
            .then((r) => {
                let data = r.data;
                if (sort === "price-asc") data = [...data].sort((a, b) => a.price - b.price);
                if (sort === "price-desc") data = [...data].sort((a, b) => b.price - a.price);
                if (sort === "rating") data = [...data].sort((a, b) => b.rating - a.rating);
                setProducts(data);
            })
            .finally(() => setLoading(false));
    }, [category, search, sort]);

    function applySearch(e) {
        e.preventDefault();
        const next = new URLSearchParams(params);
        if (q) next.set("search", q); else next.delete("search");
        setParams(next);
    }

    function setCat(c) {
        const next = new URLSearchParams(params);
        if (c) next.set("category", c); else next.delete("category");
        setParams(next);
    }

    return (
        <div data-testid="products-page">
            <section className="bg-ink text-white py-20">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <div className="overline text-primary mb-3">Shop</div>
                    <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight">
                        Products & services catalogue
                    </h1>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
                <div className="grid md:grid-cols-[240px_1fr] gap-8">
                    {/* Sidebar filters */}
                    <aside className="space-y-6">
                        <form onSubmit={applySearch} data-testid="products-search-form">
                            <label className="overline text-muted-foreground block mb-2">Search</label>
                            <div className="flex">
                                <input
                                    type="text"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search products…"
                                    className="flex-1 border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
                                    data-testid="products-search-input"
                                />
                                <button type="submit" className="bg-primary text-white px-3 hover:bg-primary-hover" data-testid="products-search-submit">
                                    <Search className="w-4 h-4" />
                                </button>
                            </div>
                        </form>

                        <div>
                            <div className="overline text-muted-foreground mb-3 flex items-center gap-2"><Filter className="w-3 h-3" /> Category</div>
                            <ul className="space-y-1">
                                {CATS.map((c) => (
                                    <li key={c.id || "all"}>
                                        <button
                                            onClick={() => setCat(c.id)}
                                            className={`w-full text-left px-3 py-2 text-sm border-l-2 transition-colors ${
                                                category === c.id ? "border-primary text-primary font-semibold bg-primary/5" : "border-transparent hover:border-primary"
                                            }`}
                                            data-testid={`filter-cat-${c.id || 'all'}`}
                                        >
                                            {c.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <div className="overline text-muted-foreground mb-3">Sort</div>
                            <select
                                value={sort}
                                onChange={(e) => {
                                    const next = new URLSearchParams(params);
                                    next.set("sort", e.target.value);
                                    setParams(next);
                                }}
                                className="w-full border border-border bg-white px-3 py-2 text-sm"
                                data-testid="products-sort"
                            >
                                {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                        </div>
                    </aside>

                    {/* Product grid */}
                    <div data-testid="products-grid">
                        <div className="text-sm text-muted-foreground mb-4">{loading ? "Loading…" : `${products.length} result${products.length === 1 ? '' : 's'}`}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {products.map((p) => {
                                const wished = has(p.id);
                                return (
                                <div key={p.id} className="group bg-white border border-border hover:border-primary transition-all relative" data-testid={`product-card-${p.slug}`}>
                                    <button
                                        onClick={() => { toggle(p); toast.success(wished ? "Removed from wishlist" : "Saved to wishlist"); }}
                                        className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${wished ? "bg-primary text-white" : "bg-white/90 hover:bg-white text-ink"}`}
                                        data-testid={`wishlist-toggle-${p.slug}`}
                                        aria-label="Toggle wishlist"
                                    >
                                        <Heart className="w-4 h-4" fill={wished ? "white" : "none"} />
                                    </button>
                                    <Link to={`/products/${p.slug}`}>
                                        <div className="aspect-square overflow-hidden bg-surface">
                                            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                        </div>
                                    </Link>
                                    <div className="p-4">
                                        <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                                            <span>{p.category}</span>
                                            <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-accent text-accent" /> {p.rating}</span>
                                        </div>
                                        <Link to={`/products/${p.slug}`}>
                                            <h3 className="font-display font-bold text-base leading-tight mb-2 hover:text-primary transition-colors line-clamp-2">{p.name}</h3>
                                        </Link>
                                        <div className="text-xs text-muted-foreground line-clamp-2 mb-3 min-h-[2rem]">{p.short_description}</div>
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-display font-bold text-xl">{format(p.price)}</span>
                                            <button
                                                onClick={() => { add(p, 1); toast.success(`${p.name} added to cart`); }}
                                                className="inline-flex items-center gap-1.5 bg-primary text-white px-3 py-2 text-xs font-semibold hover:bg-primary-hover transition-colors"
                                                data-testid={`add-to-cart-${p.slug}`}
                                            >
                                                <ShoppingCart className="w-3.5 h-3.5" /> Add
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
